import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { showErrorToastr } from "src/app/shared/helpers/toastr-helper/ToastrHelper";

interface IRecorder {
    start: (audioOnly: boolean) => void;
    stop: () => void;
    isRecording: boolean;
    isBusy: boolean;

    forceStop: () => void;
    reset: () => void;
}

interface IUseRecorderProps {
    videoLiveRef: React.RefObject<HTMLVideoElement | null>;
    onStop: (recording: File) => void;
    onStart: () => void;
}

export const videoDimensionConstraint = 480;

const audioMimeType = "audio/webm;codecs=opus";
const videoMimeType = "video/mp4";
const audioFileName = "audio";
const videoFileName = "video";
const audioFileExtension = "webm";
const videoFileExtension = "mp4";
const videoAspectRatioConstraint = 1.0; 

function useRecorder({ videoLiveRef, onStop, onStart }: IUseRecorderProps) : IRecorder {
    const { t } = useTranslation();
    const chunksRef = useRef<Blob[]>([]);
    const mediaRecorder = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);

    const forceStopped = useRef(false);
    
    const [isRecording, setIsRecording] = useState<boolean>(false);
    const [isBusy, setIsBusy] = useState<boolean>(false);
    
    useEffect(() => {
        return () => {
            stopRecorder();
        }
    }, [])

    useEffect(() => 
    {
        if (isRecording && streamRef.current?.getVideoTracks().length !== 0 && videoLiveRef.current) {
            videoLiveRef.current.srcObject = streamRef.current;
        }
    }, [isRecording, videoLiveRef])

    const tryInitMediaRecorder = async (audioOnly: boolean) : Promise<boolean> => {
        try {
            streamRef.current = await navigator.mediaDevices.getUserMedia({
                video: audioOnly ? false : {
                    height: { ideal: videoDimensionConstraint },
                    width: { ideal: videoDimensionConstraint },
                    aspectRatio: videoAspectRatioConstraint,
                },
                audio: true,
            });

            var mimeType = audioOnly ? audioMimeType : videoMimeType;
            const recorder = new MediaRecorder(streamRef.current, { mimeType });

            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    chunksRef.current.push(event.data);
                }
            };

            recorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mimeType });
                const file = new File([blob], `recorded_${audioOnly ? audioFileName : videoFileName}.${audioOnly ? audioFileExtension : videoFileExtension}`, { type: mimeType });
                chunksRef.current = [];
                if (!forceStopped.current)
                {
                    onStop(file);
                }
            };

            mediaRecorder.current = recorder;
            return true;
        } catch (err) {
            if (err instanceof Error && err.name === "NotAllowedError")
            {
                showErrorToastr(t("error.microphoneOrCameraPermissonNotGranted"));
            }
            stopRecorder();
            console.error('Error accessing media devices.', err);
            return false;
        }
    }

    function stopRecorder()
    {
        setIsRecording(false);
        mediaRecorder.current?.stop();
        streamRef.current?.getTracks().forEach(track => track.stop());
        mediaRecorder.current = null;
    }

    function startRecorder(audioOnly: boolean) {
        if (isRecording || isBusy || forceStopped.current) {
            return;
        }

        setIsBusy(true);
        tryInitMediaRecorder(audioOnly).then((isStarted: boolean) => {
            if (isStarted)
            {
                mediaRecorder.current?.start();
                setIsRecording(true);
                if (!forceStopped.current)
                {
                    onStart();
                }
            }
        })
        .finally(() =>
        {
            setIsBusy(false);
        });
    };
    
    function forceStop()
    {
        forceStopped.current = true;
        stopRecorder();
    }
    
    function reset()
    {
        forceStopped.current = false;
    }

    return {
        start: startRecorder,
        stop: stopRecorder,
        isRecording,
        isBusy: isBusy,
        forceStop,
        reset,
    }
}

export default useRecorder;